import React, { useCallback, useMemo, useRef } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { getTimePermissionModuleCapabilities } from '../../config/timePermissionCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useTimePermissionRequests } from '../../hooks/useTimePermissionRequests';
import type { TimePermissionRequestListItem } from '../../types/timePermission';
import {
  formatCalendarDate,
  formatPermissionTime,
} from '../../utils/dateTime';
import { formatDisplayLabel } from '../../utils/camelCaseLabel';
import { createDefaultTimePermissionFilters } from '../../utils/timePermissionFilters';
import { getTimePermissionStatusCategory } from '../../utils/timePermissionStatus';

type Props = NativeStackScreenProps<
  RootStackParamList,
  'TimePermissionRequests'
>;

function statusTextColor(statusCode: string | null, palette: ColorPalette) {
  const category = getTimePermissionStatusCategory(statusCode);
  if (category === 'success') return palette.success;
  if (category === 'danger') return palette.error;
  if (category === 'warning') return palette.warning;
  if (category === 'info') return palette.info;
  return palette.textSecondary;
}

function RequestRow({
  item,
  onPress,
  palette,
}: {
  item: TimePermissionRequestListItem;
  onPress: () => void;
  palette: ColorPalette;
}) {
  const dateLabel = formatCalendarDate(item.permissionDate);
  const timeRange = `${formatPermissionTime(item.fromTime)} - ${formatPermissionTime(item.toTime)}`;
  const reason =
    item.reasonSummary ||
    formatDisplayLabel(
      item.permissionTypeName || item.permissionTypeCode,
      'Permission',
    );

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${dateLabel}, status ${item.statusLabel}`}
      onPress={onPress}
      style={[styles.row, { borderBottomColor: palette.border }]}
    >
      <View style={styles.rowLeft}>
        <Text style={[styles.title, { color: palette.textPrimary }]}>
          {dateLabel}
        </Text>
        <Text style={[styles.meta, { color: palette.textSecondary }]}>
          {timeRange}
        </Text>
      </View>
      <View style={styles.rowRight}>
        <Text
          style={[
            styles.status,
            { color: statusTextColor(item.statusCode, palette) },
          ]}
        >
          {item.statusLabel}
        </Text>
        <Text style={[styles.meta, { color: palette.textSecondary }]}>
          {reason}
        </Text>
      </View>
    </Pressable>
  );
}

export function TimePermissionRequestListScreen({
  navigation,
  route,
}: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const capabilities = getTimePermissionModuleCapabilities(auth.permissions);
  const history = useTimePermissionRequests();
  const {
    applyFilters,
    loadRequests,
    refreshRequests,
    resetFilters,
    retry,
    filters,
    items,
    isLoading,
    isRefreshing,
    isLoadingMore,
    error,
    loadMore,
  } = history;
  const defaults = useMemo(() => createDefaultTimePermissionFilters(), []);
  const hasFocused = useRef(false);

  useFocusEffect(
    useCallback(() => {
      const applied = route.params?.appliedFilters;
      if (applied) {
        applyFilters(applied);
        navigation.setParams({ appliedFilters: undefined });
        hasFocused.current = true;
        return;
      }
      if (!hasFocused.current) {
        hasFocused.current = true;
        loadRequests();
        return;
      }
      refreshRequests();
    }, [
      applyFilters,
      loadRequests,
      navigation,
      refreshRequests,
      route.params?.appliedFilters,
    ]),
  );

  const filtersCustomized =
    filters.fromDate !== defaults.fromDate ||
    filters.toDate !== defaults.toDate;

  const themed = useMemo(
    () => ({
      range: {
        ...typography.body,
        color: palette.textSecondary,
        marginBottom: spacing.md,
      },
      warning: {
        ...typography.caption,
        color: palette.warning,
        marginBottom: spacing.md,
      },
    }),
    [palette],
  );

  const footer = useMemo(() => {
    if (!isLoadingMore) return null;
    return (
      <View
        style={styles.footer}
        accessibilityLabel="Loading more permission requests"
      >
        <ActivityIndicator color={palette.primary} />
      </View>
    );
  }, [isLoadingMore, palette.primary]);

  return (
    <PermissionGuard
      permission={PERMISSIONS.TIME_PERMISSION_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen contentStyle={styles.screen}>
        <ScreenHeader
          title="Permission Requests"
          onBack={navigation.goBack}
        />
        <View style={styles.body}>
          <Text style={themed.range}>
            {filters.fromDate} to {filters.toDate}
          </Text>
          <View style={styles.actions}>
            <AppButton
              title="Filters"
              variant="secondary"
              onPress={() =>
                navigation.navigate(routes.timePermissionFilter, {
                  currentFilters: filters,
                })
              }
            />
          </View>
          {!capabilities.canApply ? (
            <Text style={themed.warning}>
              Apply Permission is not currently available.
            </Text>
          ) : null}

          {isLoading && items.length === 0 ? (
            <View style={styles.center}>
              <ActivityIndicator
                color={palette.primary}
                accessibilityLabel="Loading permission requests"
              />
            </View>
          ) : error && items.length === 0 ? (
            <ErrorState message={error} onRetry={retry} />
          ) : (
            <FlatList
              data={items}
              keyExtractor={item => String(item.permissionRequestId)}
              refreshControl={
                <RefreshControl
                  refreshing={isRefreshing}
                  onRefresh={refreshRequests}
                  tintColor={palette.primary}
                />
              }
              onEndReached={loadMore}
              onEndReachedThreshold={0.4}
              ListFooterComponent={footer}
              ListEmptyComponent={
                <EmptyState
                  title="No permission requests"
                  message="No permission requests were found for this period."
                  actionTitle={filtersCustomized ? 'Reset filters' : 'Retry'}
                  onAction={filtersCustomized ? resetFilters : retry}
                />
              }
              contentContainerStyle={styles.listContent}
              renderItem={({ item }) => (
                <RequestRow
                  item={item}
                  onPress={() =>
                    navigation.navigate(routes.timePermissionDetails, {
                      permissionRequestId: item.permissionRequestId,
                    })
                  }
                  palette={palette}
                />
              )}
            />
          )}
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, paddingBottom: 0 },
  body: { flex: 1, marginTop: spacing.lg },
  actions: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  listContent: { flexGrow: 1, paddingBottom: spacing.xl },
  row: {
    paddingVertical: spacing.lg,
    borderBottomWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  rowLeft: { flex: 1, gap: spacing.xs },
  rowRight: { alignItems: 'flex-end', gap: spacing.xs, maxWidth: '48%' },
  title: { ...typography.body, fontWeight: '700' },
  meta: { ...typography.caption },
  status: { ...typography.body, fontWeight: '700' },
  footer: { paddingVertical: spacing.lg, alignItems: 'center' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
});
